import { cn } from '@/utils/cn';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/ui';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { getAccountQueryOptions } from '@/features/account/api/get-account';
import {
  getRealizedPnl,
  getSettledFunding,
  getUpdatedAccountOnFill,
} from '@/features/account/utils/math';
import {
  CapitalPlan,
  getCapitalPlansQueryOptions,
} from '@/features/vest-capital/api/get-capital-plans';
import { queryClient } from '@/lib/api-clients/query-client';
import { getCurrentAccount, useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS, useMarketStore } from '@/store/use-markets-store';
import { useSettingsStore } from '@/store/use-settings-store';
import { AccountResponse, CapitalStatus, OrderType } from '@/types';
import {
  adjustDecimals,
  formatBigInt,
  parseDecimalToBigInt,
} from '@/utils/value-format';

import { getInitMarginRatio } from '@/features/markets/utils/helpers';
import {
  getIsStatusEval,
  getIsStatusFundedOrEval,
} from '@/features/vest-capital/utils/status';

import {
  calculateSlippageAmount,
  getBboPrice,
  getEstFillPrice,
  getFee,
  getSlippage,
} from '../utils/math';
import { getProjectedMidSlippageAmount } from '../utils/slippage-estimation';
import { OrderFormSchema } from './order-form';

export type LimitsBreachReason =
  | 'MAX_DAILY_LOSS'
  | 'MAX_DRAWDOWN'
  | 'MAINT_MARGIN'
  | 'BREACH_PROXIMITY';

const BREACH_REASON_LABELS: Record<LimitsBreachReason, string> = {
  MAX_DAILY_LOSS: 'Max Daily Loss',
  MAX_DRAWDOWN: 'Max Drawdown',
  MAINT_MARGIN: 'Maintenance Margin',
  BREACH_PROXIMITY: 'Breach Proximity',
};

// Warn when the order's top-of-book half-spread execution cost reaches
// 1/BREACH_PROXIMITY_RATIO (50%) of the funds left before an account breach.
const BREACH_PROXIMITY_RATIO = 2n;

export interface LimitsBreachInfo {
  reasons: LimitsBreachReason[];
  estRealizedPnl?: bigint;
  postFillValue?: bigint;
  postFillDailyLoss?: bigint;
  maxDailyLoss?: bigint;
  postFillDrawdown?: bigint;
  maxDrawdown?: bigint;
  maintMarginReq?: bigint;
  breachDistance?: bigint;
}

export const getLimitsBreachInfo = ({
  order,
}: OrderFormSchema): LimitsBreachInfo | null => {
  if (order.orderType !== OrderType.MARKET) {
    return null;
  }

  const { showLimitsBreachConfirm } = useSettingsStore.getState();

  if (!showLimitsBreachConfirm) {
    return null;
  }

  const { book, marketSpec, marketData } = useMarketStore(
    order.symbol,
  ).getState();

  const size = parseDecimalToBigInt(order.size, marketSpec.sizeDecimals);
  const { address, getCapitalStatus, getAuthParams } =
    useAccountStore.getState();
  const capitalStatus = getCapitalStatus(address);
  const isEval = getIsStatusEval(capitalStatus);

  if (size === 0n) {
    return null;
  }

  const estFillPrice = getEstFillPrice(
    order.isBuy,
    size,
    book,
    marketSpec.priceDecimals,
    isEval,
  );

  if (!estFillPrice?.bigint) {
    return null;
  }

  if (!book.asks.length || !book.bids.length) {
    return null;
  }

  const account = getCurrentAccount();
  const accountingFillPrice = isEval
    ? getBboPrice(order.symbol, order.isBuy)
    : estFillPrice.enoughLiquidity
      ? estFillPrice.bigint
      : parseDecimalToBigInt(order.limitPrice, marketSpec.priceDecimals);
  if (accountingFillPrice === null || accountingFillPrice <= 0n) {
    return null;
  }
  const initMarginRatio = getInitMarginRatio(order.symbol);
  const cumFunding = marketData.cumFunding ?? 0n;
  const fees = getFee(order.symbol, size, accountingFillPrice).bigint;
  const settledFunding = getSettledFunding(order.symbol, account, {
    cumFunding,
  }).bigint;
  const estRealizedPnl = getRealizedPnl(
    order.symbol,
    {
      isBuy: order.isBuy,
      settledFunding,
      fees,
      lastFilledPrice: accountingFillPrice,
      lastFilledSize: size,
    },
    account.positions[order.symbol],
  ).bigint;

  const updatedAccount = getUpdatedAccountOnFill(
    order.symbol,
    order.isBuy,
    size,
    accountingFillPrice,
    estRealizedPnl,
    initMarginRatio,
    account,
    {
      cumFunding,
    },
  );

  const postFillValue = updatedAccount.value.bigint;

  const reasons: LimitsBreachReason[] = [];
  let postFillDailyLoss: bigint | undefined;
  let maxDailyLoss: bigint | undefined;
  let postFillDrawdown: bigint | undefined;
  let maxDrawdown: bigint | undefined;
  let breachFloor: bigint | undefined;

  if (
    capitalStatus !== CapitalStatus.UNKNOWN &&
    capitalStatus !== CapitalStatus.NOT_ELIGIBLE
  ) {
    const apiKey = getAuthParams(address)?.apiKey;
    const accountData = queryClient.getQueryData<AccountResponse>(
      getAccountQueryOptions(address, apiKey).queryKey,
    );
    const capitalPlans = queryClient.getQueryData<CapitalPlan[]>(
      getCapitalPlansQueryOptions().queryKey,
    );

    if (accountData?.capital && capitalPlans) {
      const activePlanMetadata = capitalPlans.find(
        (plan) => plan.name === accountData.capital?.activeCapitalPlan,
      );

      if (activePlanMetadata) {
        const startingBalance = parseDecimalToBigInt(
          (Number(activePlanMetadata.metadata.account_size) / 100).toString(),
          COLLATERAL_DECIMALS,
        );
        const maxDailyLossPct = Number(
          activePlanMetadata.metadata.max_daily_loss,
        );
        const maxDrawdownLimit = parseDecimalToBigInt(
          accountData.capital.maxDrawdownLimit,
          COLLATERAL_DECIMALS,
        );
        const maxDailyLossLimit = parseDecimalToBigInt(
          accountData.capital.maxDailyLossLimit,
          COLLATERAL_DECIMALS,
        );
        const priorDayAccountValue = parseDecimalToBigInt(
          accountData.capital.priorDayAccountValue ??
            accountData.capital.priorDayBalance,
          COLLATERAL_DECIMALS,
        );

        const priorDayMaxDailyLossLimit =
          (priorDayAccountValue * (100n - BigInt(maxDailyLossPct))) / 100n;
        const withdrawnAmount = priorDayMaxDailyLossLimit - maxDailyLossLimit;

        const dailyFloor = maxDailyLossLimit - withdrawnAmount;
        breachFloor =
          dailyFloor > maxDrawdownLimit ? dailyFloor : maxDrawdownLimit;

        maxDrawdown = startingBalance - maxDrawdownLimit;
        maxDailyLoss =
          priorDayAccountValue - maxDailyLossLimit - withdrawnAmount;

        postFillDailyLoss =
          priorDayAccountValue - postFillValue - withdrawnAmount;
        postFillDrawdown =
          postFillValue < startingBalance
            ? startingBalance - postFillValue
            : 0n;

        if (postFillDailyLoss >= maxDailyLoss) {
          reasons.push('MAX_DAILY_LOSS');
        }

        if (postFillDrawdown >= maxDrawdown) {
          reasons.push('MAX_DRAWDOWN');
        }
      }
    }
  }

  if (postFillValue <= updatedAccount.maintMarginReq.bigint) {
    reasons.push('MAINT_MARGIN');
  }

  const breachFloorValue = breachFloor ?? account.maintMarginReq.bigint;
  const breachDistance = account.value.bigint - breachFloorValue;
  const spread = book.asks[0][0] - book.bids[0][0];
  const halfSpreadCost =
    adjustDecimals(
      size * (spread > 0n ? spread : 0n),
      COLLATERAL_DECIMALS - marketSpec.priceDecimals - marketSpec.sizeDecimals,
    ) / 2n;

  if (
    breachDistance > 0n &&
    halfSpreadCost * BREACH_PROXIMITY_RATIO >= breachDistance
  ) {
    reasons.push('BREACH_PROXIMITY');
  }

  if (reasons.length === 0) {
    return null;
  }

  return {
    reasons,
    estRealizedPnl,
    postFillValue,
    postFillDailyLoss,
    maxDailyLoss,
    postFillDrawdown,
    maxDrawdown,
    maintMarginReq: updatedAccount.maintMarginReq.bigint,
    breachDistance,
  };
};

interface LimitsBreachConfirmProps {
  open: boolean;
  breachInfo: LimitsBreachInfo | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
}

const LimitsBreachConfirm = (props: LimitsBreachConfirmProps) => {
  const { open, breachInfo, onSubmit, onOpenChange } = props;

  const { watch, formState } = useFormContext<OrderFormSchema>();
  const { isSubmitting } = formState;
  const symbol = watch('order.symbol');
  const isBuy = watch('order.isBuy');
  const { isEval, isFundedOrEval } = useAccountStore(
    useShallow((state) => {
      const capitalStatus = state.getCapitalStatus(state.address);
      return {
        isEval: getIsStatusEval(capitalStatus),
        isFundedOrEval: getIsStatusFundedOrEval(capitalStatus),
      };
    }),
  );

  const { book, marketSpec, midPrice, slippageReferencePrice } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      book: state.book,
      slippageReferencePrice: getBboPrice(symbol, isBuy),
      marketSpec: state.marketSpec,
      midPrice: state.marketData.midPrice,
    })),
  );
  const { priceDecimals, sizeDecimals } = marketSpec;

  const size = parseDecimalToBigInt(watch('order.size'), sizeDecimals);

  const { estFillPrice, slippage, slippageAmount } = useMemo(() => {
    const estFillPriceResult = getEstFillPrice(
      isBuy,
      size,
      book,
      priceDecimals,
      isEval,
    );

    if (!estFillPriceResult) {
      return {
        estFillPrice: undefined,
        slippage: undefined,
        slippageAmount: null,
      };
    }

    const slippageResult = estFillPriceResult.bigint
      ? getSlippage(
          symbol,
          isBuy,
          estFillPriceResult.bigint,
          isEval
            ? undefined
            : {
                size,
                visibleCost: estFillPriceResult.visibleCost,
                visibleFilled: estFillPriceResult.visibleFilled,
                book,
              },
        )
      : undefined;

    return {
      estFillPrice: estFillPriceResult,
      slippage: slippageResult,
      slippageAmount: isEval
        ? calculateSlippageAmount(isBuy, size, slippageReferencePrice, midPrice)
        : getProjectedMidSlippageAmount(
            isBuy,
            size,
            estFillPriceResult.visibleCost,
            estFillPriceResult.visibleFilled,
            book,
          ),
    };
  }, [
    isBuy,
    size,
    book,
    priceDecimals,
    slippageReferencePrice,
    symbol,
    isEval,
    midPrice,
  ]);

  const formattedSlippage = isFundedOrEval
    ? slippageAmount === null
      ? '—'
      : formatBigInt(slippageAmount, priceDecimals + sizeDecimals, {
          digits: 2,
          style: 'currency',
        })
    : slippage
      ? `${slippage.decimal}%`
      : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="text-xl text-foreground">
            Limits Breach Warning
          </DialogTitle>

          <DialogDescription>
            The expected slippage on this trade is high enough to potentially
            breach your risk limits. This could result in an account fail.
            Consider adjusting your order size.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5">
          {estFillPrice?.bigint !== null &&
            estFillPrice?.bigint !== undefined && (
              <div className="flex h-8 items-center justify-between">
                <p className="flex items-center gap-2">Fill Price</p>

                <span className="leading-tight text-foreground">
                  {formatBigInt(estFillPrice.bigint, priceDecimals, {
                    digits: Number(priceDecimals),
                  })}
                </span>
              </div>
            )}

          <div className="flex h-8 items-center justify-between">
            <p className="flex items-center gap-2">Slippage</p>

            <span className="leading-tight text-red">{formattedSlippage}</span>
          </div>

          {breachInfo && breachInfo.reasons.length > 0 && (
            <>
              <div className="my-4 border-t border-border" />

              <div className="flex h-8 items-center justify-between">
                <p className="flex items-center gap-2">Breach Reason</p>

                <span className="text-end leading-tight">
                  {breachInfo.reasons
                    .map((r) => BREACH_REASON_LABELS[r])
                    .join(', ')}
                </span>
              </div>

              {breachInfo.estRealizedPnl !== undefined && (
                <div className="flex h-8 items-center justify-between">
                  <p className="flex items-center gap-2">Est. Realized PnL</p>

                  <span
                    className={cn(
                      'leading-tight text-muted-foreground',
                      breachInfo.estRealizedPnl < 0n && 'text-red',
                      breachInfo.estRealizedPnl > 0n && 'text-green',
                    )}
                  >
                    {formatBigInt(
                      breachInfo.estRealizedPnl,
                      COLLATERAL_DECIMALS,
                      { digits: 2, showChange: true },
                    )}
                  </span>
                </div>
              )}

              {breachInfo.postFillValue !== undefined && (
                <div className="flex h-8 items-center justify-between">
                  <p className="flex items-center gap-2">
                    Est. Post-Fill Account Value
                  </p>

                  <span className="leading-tight text-foreground">
                    {formatBigInt(
                      breachInfo.postFillValue,
                      COLLATERAL_DECIMALS,
                      { digits: 2 },
                    )}
                  </span>
                </div>
              )}

              {breachInfo.reasons.includes('MAX_DAILY_LOSS') &&
                breachInfo.postFillDailyLoss !== undefined &&
                breachInfo.maxDailyLoss !== undefined && (
                  <div className="flex h-8 items-center justify-between">
                    <p className="flex items-center gap-2">Est. Daily Loss</p>

                    <span className="leading-tight text-red">
                      {formatBigInt(
                        breachInfo.postFillDailyLoss,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                      {' / '}
                      {formatBigInt(
                        breachInfo.maxDailyLoss,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                    </span>
                  </div>
                )}

              {breachInfo.reasons.includes('MAX_DRAWDOWN') &&
                breachInfo.postFillDrawdown !== undefined &&
                breachInfo.maxDrawdown !== undefined && (
                  <div className="flex h-8 items-center justify-between">
                    <p className="flex items-center gap-2">Est. Drawdown</p>

                    <span className="leading-tight text-red">
                      {formatBigInt(
                        breachInfo.postFillDrawdown,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                      {' / '}
                      {formatBigInt(
                        breachInfo.maxDrawdown,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                    </span>
                  </div>
                )}

              {breachInfo.reasons.includes('MAINT_MARGIN') &&
                breachInfo.maintMarginReq !== undefined &&
                breachInfo.postFillValue !== undefined && (
                  <div className="flex h-8 items-center justify-between">
                    <p className="flex items-center gap-2">Maint. Margin</p>

                    <span className="leading-tight text-red">
                      {formatBigInt(
                        breachInfo.postFillValue,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                      {' / '}
                      {formatBigInt(
                        breachInfo.maintMarginReq,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                    </span>
                  </div>
                )}

              {breachInfo.reasons.includes('BREACH_PROXIMITY') &&
                breachInfo.breachDistance !== undefined && (
                  <div className="flex h-8 items-center justify-between">
                    <p className="flex items-center gap-2">
                      Funds Before Breach
                    </p>

                    <span className="leading-tight text-red">
                      {formatBigInt(
                        breachInfo.breachDistance,
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )}
                    </span>
                  </div>
                )}
            </>
          )}

          <div className="mt-2 flex h-10 items-center gap-3">
            <Checkbox
              id="limits-breach-confirm"
              className="mt-0.5 size-5"
              onCheckedChange={(checked) =>
                useSettingsStore.setState({
                  showLimitsBreachConfirm: !checked,
                })
              }
            />

            <Label
              htmlFor="limits-breach-confirm"
              className="cursor-pointer text-base leading-none text-vestgrey-600"
            >
              Don't show this again
            </Label>
          </div>
        </div>

        <DialogFooter className="grid pt-2 sm:justify-normal">
          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              className="w-full rounded-none uppercase tracking-wider"
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Submit Order
            </Button>
          </DialogClose>

          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              variant="ghost"
              className="w-full rounded-none uppercase tracking-wider"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LimitsBreachConfirm;
